/**
 * Supply Chain & Ops Agent - Coming Soon
 * 
 * Clean, polished design following PIQ standards (ML Command Center style)
 * Real data from APIs when available - no fake data
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import GlobalHeader from '../../components/GlobalHeader';
import ExampleQuestions from '@/components/agents/ExampleQuestions';
import { 
  Loader2, 
  Truck,
  AlertTriangle,
  Package,
  RefreshCw,
  ArrowLeftRight,
  Zap,
  Brain,
  Clock,
  Rocket,
  CheckCircle2,
  ChevronRight,
  Warehouse,
  TrendingUp,
  BarChart3,
  Activity,
  Archive
} from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

const EXAMPLE_QUESTIONS = [
  "Where are we at risk of stockouts in the next 4 weeks?",
  "Where are we holding too much inventory?",
  "What are our low stock risks right now?",
  "How should we allocate DC inventory?",
  "Give me a supply chain report"
];

const SupplyChain: React.FC = () => {
  const { selectedRetailer } = useTenant();
  const [loading, setLoading] = useState(true);
  const [dataAvailable, setDataAvailable] = useState(false);

  useEffect(() => {
    checkDataStatus();
  }, [selectedRetailer?.id]);

  const checkDataStatus = async () => {
    if (!selectedRetailer?.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`/api/supply-chain/report?retailer_id=${selectedRetailer.id}`).then(r => r.json());
      setDataAvailable(res.success && (res.report?.lowStockRisks?.alerts?.length > 0 || res.report?.demandSupplyMismatch?.mismatches?.length > 0));
    } catch {
      setDataAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  // Connected agents
  const connectedAgents = [
    { title: 'Inventory Intelligence', href: '/ai/inventory-intelligence', icon: TrendingUp, gradient: 'from-green-500 to-emerald-600' },
    { title: 'Inventory Deep Dive', href: '/inventory', icon: Archive, gradient: 'from-blue-500 to-cyan-600' },
    { title: 'Merch Optimization', href: '/agents/merch-optimization', icon: Package, gradient: 'from-orange-500 to-amber-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="Supply Chain & Ops"
        subtitle="Low stock risk, demand vs supply mismatch & DC allocation optimization"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Status Banner */}
        <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-amber-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">Supply Chain Intelligence</h2>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-sm px-3 py-1">
                      Coming Soon
                    </Badge>
                  </div>
                  <p className="text-gray-600 max-w-xl">
                    AI-powered supply chain analysis will predict stockout risks, identify demand-supply 
                    mismatches, and optimize DC allocation from your actual inventory data.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={checkDataStatus} 
                disabled={loading}
                className="bg-white hover:bg-gray-50 shadow-sm"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Check Status
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-red-700">Critical Alerts</span>
              <div className="p-3 bg-red-500 rounded-xl shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-red-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-red-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting detection</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-orange-700">High Alerts</span>
              <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                <Activity className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-orange-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-orange-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting analysis</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-blue-700">Distribution Issues</span>
              <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                <ArrowLeftRight className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-blue-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting detection</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-700">Overstock Issues</span>
              <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                <Warehouse className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-purple-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting detection</span>
            </div>
          </div>
        </div>

        {/* Main Coming Soon Card */}
        <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Supply Chain Intelligence Hub</h3>
                <p className="text-slate-300">Powered by your inventory data • Proactive risk detection</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-8">
            {/* Capabilities Grid */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-amber-500" />
                What you'll get when activated
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl border-2 border-red-100 bg-gradient-to-br from-red-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-red-100">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Stockout Prediction</h5>
                      <p className="text-sm text-gray-600">ML predicts which SKUs will stockout 2-4 weeks in advance</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-blue-100">
                      <ArrowLeftRight className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Demand-Supply Mismatch</h5>
                      <p className="text-sm text-gray-600">Identify DC-to-store distribution imbalances</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-amber-100 bg-gradient-to-br from-amber-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-amber-100">
                      <Warehouse className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">DC Allocation</h5>
                      <p className="text-sm text-gray-600">Optimize inventory placement across distribution centers</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                <div className="p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-purple-100">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Revenue Protection</h5>
                      <p className="text-sm text-gray-600">Prevent lost sales by keeping high-velocity SKUs in stock</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                How Supply Chain Intelligence Works
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Analyze Inventory</h5>
                    <p className="text-sm text-gray-600">ML scans EOH, WOS, velocity, and DC levels daily</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Detect Risks</h5>
                    <p className="text-sm text-gray-600">Predict stockouts and identify distribution gaps</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Recommend Actions</h5>
                    <p className="text-sm text-gray-600">Get specific reorder and redistribution recommendations</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Questions */}
        <ExampleQuestions 
          questions={EXAMPLE_QUESTIONS}
          accentColor="amber"
          title="Ask the Supply Chain Agent"
          description="Click any question to analyze inventory and supply"
        />

        {/* Connected Agents */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connectedAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link key={agent.href} href={agent.href}>
                  <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-amber-300 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-semibold text-gray-900">{agent.title}</span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-amber-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Roadmap Preview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Supply Chain Roadmap</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 shadow-sm">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Risk Alerts</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Proactive stockout warnings</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm">
                  <ArrowLeftRight className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Redistribution</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">DC-to-store optimization</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                  <Truck className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Reorder Engine</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Smart order recommendations</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-sm">
                  <BarChart3 className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">WOS Analytics</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Weeks of supply tracking</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SupplyChain;
